import { useState } from 'react';
import Link from 'next/link';
import { useSnackbar } from 'notistack';
import graphQLClient from '../../../helpers/graphQLClient';
import openV2Editor from '../../../helpers/openV2Editor';
import { fraunces } from '../../Landing/fonts';
import { Eyebrow, INK, MUTED, PAPER_CARD } from '../../Landing/ui';
import {
  PREPARE_HOSTING_V2,
  REQUEST_HOSTING_V2_SWITCH,
} from '../../../helpers/graphql/hosting';

// How far along the switch a status is, so an optimistic local step is never
// overwritten by a slower server read (and never moves backwards).
const ORDER = [undefined, null, 'available', 'preview', 'requested', 'active'];

function BannerButton({ onClick, disabled, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-rounded-full tw-border-0 tw-cursor-pointer tw-px-6 tw-py-2.5 tw-text-sm tw-font-semibold tw-bg-primary-600 tw-text-white hover:tw-bg-primary-700 tw-shadow-[0_10px_30px_-10px_rgba(0,137,123,0.7)] tw-transition-all tw-duration-200 hover:-tw-translate-y-0.5 disabled:tw-opacity-60 disabled:tw-cursor-wait"
    >
      {children}
    </button>
  );
}

// The secondary action, sized like BannerButton so a pair lines up.
function OutlineButton({ onClick, disabled, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-rounded-full tw-cursor-pointer tw-px-6 tw-py-2.5 tw-text-sm tw-font-semibold tw-bg-transparent tw-border tw-border-solid tw-border-[#C9C0AA] dark:tw-border-[#3A4D44] ${INK} hover:tw-border-primary-600 hover:tw-text-primary-700 dark:hover:tw-text-primary-300 tw-transition-colors disabled:tw-opacity-60 disabled:tw-cursor-wait`}
    >
      {children}
    </button>
  );
}

// The v1→v2 switch offer, rendered purely from blog.hostingV2Status:
// "available" → offer a preview; "preview" → open the preview editor or
// switch; "requested" → a switch that has not completed yet (the mutation
// performs the flip instantly and idempotently, so this state only shows
// after a failed attempt — the button here simply runs it again). Rendered
// as the discovery banner ABOVE the v1 tabs — every owner sees it on
// arrival. An active blog renders the v2 panel instead, never this offer.
export default function HostingV2Card({ blog, blogId, refetchHostingPlan }) {
  // This banner survives tab changes, but the optimistic bridge stays: it
  // covers the window until the refetched hosting plan carries the new
  // status; whichever is further along wins.
  const [localStatus, setLocalStatus] = useState(null);
  // One action at a time; the label changes only on the button that runs.
  const [action, setAction] = useState(null);
  const [confirming, setConfirming] = useState(false);
  // The last failed switch says what to do (a DNS record, a retry); a toast
  // would vanish before anyone could copy it.
  const [notice, setNotice] = useState(null);
  const { enqueueSnackbar } = useSnackbar();

  const status = ORDER.indexOf(localStatus) > ORDER.indexOf(blog.hostingV2Status)
    ? localStatus
    : blog.hostingV2Status;
  if (!status || status === 'active') return null;
  const busy = action !== null;

  const run = (name, mutation, nextStatus) => {
    setAction(name);
    setNotice(null);
    graphQLClient(mutation, { blogId })
      .then(res => {
        const result =
          res?.prepareHostingV2 || res?.requestHostingV2Switch || {};
        if (result.success) {
          enqueueSnackbar(result.message || 'Done', { variant: 'success' });
          // The switch flips instantly server-side, so its buttons pass no
          // optimistic status — the refetched hosting plan is the truth.
          if (nextStatus) setLocalStatus(nextStatus);
        } else {
          setNotice(result.message || 'Something went wrong. Please try again.');
        }
        // Refetch either way: a failed switch leaves the blog 'requested'.
        if (refetchHostingPlan) refetchHostingPlan().catch(() => {});
        setConfirming(false);
        setAction(null);
      })
      .catch(err => {
        console.error(err);
        setNotice('Something went wrong. Please try again.');
        setConfirming(false);
        setAction(null);
      });
  };

  const openPreview = () => {
    setAction('open');
    openV2Editor(blogId)
      .then(opened => {
        if (!opened)
          enqueueSnackbar(
            'Your preview is still being prepared — try again in a minute.',
            { variant: 'info' },
          );
        setAction(null);
      })
      .catch(err => {
        console.error(err);
        enqueueSnackbar('Could not open the preview — please try again.', {
          variant: 'error',
        });
        setAction(null);
      });
  };

  const switchBlog = () => run('switch', REQUEST_HOSTING_V2_SWITCH, null);

  const previewAddress = blog.hostingV2PreviewUrl && (
    <p className={`${MUTED} tw-mt-3 tw-mb-0 tw-text-sm tw-leading-relaxed`}>
      You can also open your preview directly at{' '}
      <a
        href={blog.hostingV2PreviewUrl}
        target="_blank"
        rel="nofollow noreferrer noopener"
        className="hover:tw-text-primary-500 dark:hover:tw-text-primary-300 hover:tw-underline"
      >
        {blog.hostingV2PreviewUrl.replace(/^https:\/\//, '')}
      </a>
      {blog.hostingV2PreviewCode && (
        <>
          {' '}
          with the code <strong>{blog.hostingV2PreviewCode}</strong>. It stays
          private and out of search until you switch.
        </>
      )}
    </p>
  );

  const noticeBox = notice && (
    <p
      role="alert"
      className="tw-mt-4 tw-mb-0 tw-max-w-2xl tw-rounded-xl tw-px-4 tw-py-3 tw-text-sm tw-leading-relaxed tw-break-words tw-bg-[#FBEFE6] tw-text-[#7A3B12] dark:tw-bg-[#3A2A1E] dark:tw-text-[#F2C9A8]"
    >
      {notice}
    </p>
  );

  const heading = text => (
    <h3
      className={`${fraunces.className} ${INK} tw-m-0 tw-mb-2 tw-text-xl tw-font-semibold tw-tracking-tight`}
    >
      {text}
    </h3>
  );

  return (
    <div className={`${PAPER_CARD} tw-rounded-2xl tw-p-6 tw-mb-4`}>
      <Eyebrow className="tw-mb-2">TravelFeed Hosting 2.0</Eyebrow>
      {status === 'available' && (
        <>
          {heading('See your blog on Hosting 2.0')}
          <p className={`${MUTED} tw-mt-0 tw-mb-4 tw-max-w-2xl tw-leading-relaxed`}>
            A faster blog with new designs and a new visual editor — design
            changes go live in seconds. Your current site keeps running
            unchanged until you decide to switch.
          </p>
          <BannerButton
            disabled={busy}
            onClick={() => run('prepare', PREPARE_HOSTING_V2, 'preview')}
          >
            {action === 'prepare' ? 'Preparing…' : 'Create my preview'}
          </BannerButton>
          {noticeBox}
        </>
      )}
      {status === 'preview' && !confirming && (
        <>
          {heading('Your Hosting 2.0 preview')}
          <p className={`${MUTED} tw-mt-0 tw-mb-4 tw-max-w-2xl tw-leading-relaxed`}>
            Explore and fine-tune your blog in the new editor — your live blog
            stays on the current hosting until you switch. A new preview takes
            about a minute to set up. Switching keeps your address and all
            your posts.
          </p>
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
            <BannerButton disabled={busy} onClick={openPreview}>
              {action === 'open' ? 'Opening…' : 'Open preview'}
            </BannerButton>
            <OutlineButton
              disabled={busy}
              onClick={() => {
                setNotice(null);
                setConfirming(true);
              }}
            >
              Switch my blog
            </OutlineButton>
          </div>
          {noticeBox}
          {previewAddress}
        </>
      )}
      {status === 'preview' && confirming && (
        <>
          {heading('Switch your blog to Hosting 2.0?')}
          <p className={`${MUTED} tw-mt-0 tw-mb-4 tw-max-w-2xl tw-leading-relaxed`}>
            Your blog moves to Hosting 2.0 right away and stays there — there
            is no way back to the current hosting. Your address, your posts
            and every link to them stay the same, and your preview design goes
            live.
          </p>
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
            <BannerButton disabled={busy} onClick={switchBlog}>
              {action === 'switch' ? 'Switching…' : 'Yes, switch my blog'}
            </BannerButton>
            <OutlineButton
              disabled={busy}
              onClick={() => setConfirming(false)}
            >
              Not yet
            </OutlineButton>
          </div>
        </>
      )}
      {status === 'requested' && (
        <>
          {heading('Almost there')}
          <p className={`${MUTED} tw-mt-0 tw-mb-4 tw-max-w-2xl tw-leading-relaxed`}>
            Your switch to Hosting 2.0 hasn&apos;t completed yet. Your site
            stays online on the current hosting until it does — try again
            below, and{' '}
            <Link
              href="/about/contact"
              className="tw-underline hover:tw-text-primary-500 dark:hover:tw-text-primary-300"
            >
              contact us
            </Link>{' '}
            if it keeps failing.
          </p>
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
            <BannerButton disabled={busy} onClick={switchBlog}>
              {action === 'switch' ? 'Switching…' : 'Complete my switch'}
            </BannerButton>
            <OutlineButton
              disabled={busy}
              onClick={openPreview}
            >
              {action === 'open' ? 'Opening…' : 'Open preview'}
            </OutlineButton>
          </div>
          {noticeBox}
          {previewAddress}
        </>
      )}
    </div>
  );
}
